import { useCallback, useMemo, useState } from 'react';

import { parseJson, type JsonParseCode } from '@/shared/utils/parse-json/parse-json.util';

import { type KeyValueKind, type KeyValueScalar } from '../types/key-value.types';
import { type UseKeyValueCatalogue } from '../useKeyValueCatalogue/useKeyValueCatalogue';

export type KeyValueImportMode = 'merge' | 'replace';

export type KeyValueImportPlan =
  | { status: 'empty' }
  | { status: 'invalid'; code: JsonParseCode; offset: number | null }
  | { status: 'notAnObject' }
  | {
      status: 'ready';
      next: Record<string, KeyValueScalar>;
      added: string[];
      replaced: string[];
      removed: string[];
      /** Refused because a restricted catalogue does not accept them. */
      unknownKeys: string[];
      /** Refused because the value cannot be what its key holds. */
      wrongKind: string[];
    };

export interface UseKeyValueImportInput {
  currentMap: Readonly<Record<string, KeyValueScalar>>;
  catalogue: UseKeyValueCatalogue;
  onApply: (next: Record<string, KeyValueScalar>) => void;
}

export interface UseKeyValueImport {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  text: string;
  setText: (text: string) => void;
  mode: KeyValueImportMode;
  setMode: (mode: KeyValueImportMode) => void;
  plan: KeyValueImportPlan;
  canApply: boolean;
  /** Applies the plan and returns how many entries changed. */
  apply: () => number;
}

function isScalar(value: unknown): value is KeyValueScalar {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function fitsKind(
  value: KeyValueScalar,
  kind: KeyValueKind,
  options: readonly { value: string }[] | undefined,
): boolean {
  if (kind === 'number') return typeof value === 'number';
  if (kind === 'boolean') return typeof value === 'boolean';
  if (kind === 'select') {
    return (
      typeof value === 'string' &&
      (options === undefined || options.some((option) => option.value === value))
    );
  }
  return typeof value === 'string';
}

/**
 * Pasting a whole object in, instead of typing twelve rows.
 *
 * The clipboard is deliberately **not read** for the operator:
 * `navigator.clipboard.readText()` is not exposed to web content in Firefox at
 * all, is gated behind a paste gesture in Safari, and is refused everywhere on
 * an insecure origin. A control whose primary action fails on two of three
 * engines is not a control — so the operator pastes into a box, and the box is
 * something they can see and correct.
 *
 * Nothing is ever silently dropped: a key a restricted catalogue does not accept
 * and a value that cannot be what its key holds are both listed back, by name,
 * before anything is applied.
 */
export function useKeyValueImport({
  currentMap,
  catalogue,
  onApply,
}: UseKeyValueImportInput): UseKeyValueImport {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [mode, setMode] = useState<KeyValueImportMode>('merge');

  const { isKeyAllowed, kindFor, entryFor, isRequired } = catalogue;

  const plan = useMemo<KeyValueImportPlan>(() => {
    if (text.trim() === '') return { status: 'empty' };

    const parsed = parseJson(text);
    if (!parsed.ok) return { status: 'invalid', code: parsed.code, offset: parsed.offset };

    const root = parsed.value;
    if (typeof root !== 'object' || root === null || Array.isArray(root)) {
      return { status: 'notAnObject' };
    }

    const accepted: Record<string, KeyValueScalar> = {};
    const unknownKeys: string[] = [];
    const wrongKind: string[] = [];

    for (const [key, raw] of Object.entries(root)) {
      if (!isKeyAllowed(key)) {
        unknownKeys.push(key);
        continue;
      }
      if (!isScalar(raw) || !fitsKind(raw, kindFor(key), entryFor(key)?.options)) {
        wrongKind.push(key);
        continue;
      }
      accepted[key] = raw;
    }

    const next: Record<string, KeyValueScalar> =
      mode === 'merge'
        ? { ...currentMap, ...accepted }
        : {
            // A required key is not the paste's to remove: its row carries no
            // remove affordance either, so letting a replace delete it would be
            // the one door left open on a rule the rest of the editor keeps.
            ...Object.fromEntries(
              Object.entries(currentMap).filter(
                ([key]) => isRequired(key) && !Object.hasOwn(accepted, key),
              ),
            ),
            ...accepted,
          };

    const added = Object.keys(next).filter((key) => !Object.hasOwn(currentMap, key));
    const replaced = Object.keys(next).filter(
      (key) => Object.hasOwn(currentMap, key) && !Object.is(currentMap[key], next[key]),
    );
    const removed = Object.keys(currentMap).filter((key) => !Object.hasOwn(next, key));

    return { status: 'ready', next, added, replaced, removed, unknownKeys, wrongKind };
  }, [currentMap, entryFor, isKeyAllowed, isRequired, kindFor, mode, text]);

  const changeCount =
    plan.status === 'ready' ? plan.added.length + plan.replaced.length + plan.removed.length : 0;

  const close = useCallback(() => {
    setIsOpen(false);
    // Cleared on close, not on open: a dialog that reopens holding the last
    // paste invites applying it twice by muscle memory.
    setText('');
  }, []);

  const apply = useCallback((): number => {
    if (plan.status !== 'ready' || changeCount === 0) return 0;

    onApply(plan.next);
    close();

    return changeCount;
  }, [changeCount, close, onApply, plan]);

  return {
    isOpen,
    open: useCallback(() => {
      setIsOpen(true);
    }, []),
    close,
    text,
    setText,
    mode,
    setMode,
    plan,
    canApply: changeCount > 0,
    apply,
  };
}
